import React, { useState, useEffect, useMemo, useRef } from 'react';
import * as XLSX from 'xlsx';
import {
    Box, Typography, TextField, Button, Grid, IconButton,
    Card, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Chip, InputAdornment, Dialog,
    DialogContent, DialogActions, Avatar, Autocomplete, Paper, Tooltip,
    CircularProgress, ToggleButton, ToggleButtonGroup, Checkbox,
} from '@mui/material';
import http, { apiErrorMessage } from '../../../Api/http';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutlineRounded';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PersonOutlineIcon from '@mui/icons-material/PersonOutlineRounded';
import DownloadIcon from '@mui/icons-material/Download';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import SnackBar from '../../SnackBar';
import {
    getEmployees, getEmployeesWithoutSalaryStructure, postSalaryStructure, salaryStructureDashboard,
    updateSalaryStructureByEmployeeCode, deleteSalaryStructureByEmployeeCode,
    getDeductionsAndCompliance, getSalaryComponentConfig,
} from '../../../Api/Api';
import { useSelector } from 'react-redux';
import { SkeletonRows } from '../../uiKit';
import { useCrumbActionsSetter } from '../../../utils/crumbActions';
import { selectWebsiteSettings } from '../../../redux/slices/websiteSettingsSlice';

const PRIMARY = '#135F93';
const PRIMARY_LIGHT = '#E4F0F9';
const PRIMARY_DARK = '#0E4A75';
const PRIMARY_BORDER = '#B3D3E8';

const AVATAR_PALETTE = ['#0E7490', '#1D4ED8', '#C2410C', '#0E4A75', '#1D4ED8', '#BE185D', '#A16207', '#0F766E'];
const avatarColorFor = (name = '') => {
    const code = (name.charCodeAt(0) || 0) + (name.charCodeAt(1) || 0);
    return AVATAR_PALETTE[code % AVATAR_PALETTE.length];
};

const getInitials = (name = '') =>
    name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();

const formatINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
const formatINR2 = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

const DEFAULT_SPLIT = { basicPercent: 45, daPercent: 5, hraPercent: 20, conveyancePercent: 5, specialPercent: 25 };
const SPLIT_PARTS = [
    { key: 'basicPercent', short: 'Basic' },
    { key: 'daPercent', short: 'DA' },
    { key: 'hraPercent', short: 'HRA' },
    { key: 'conveyancePercent', short: 'Conv.' },
    { key: 'specialPercent', short: 'Special' },
];

// Full-month gross for a row: the API's preview, else derived from pay type.
const grossOf = (s) => {
    if (s?.grossSalary != null) return Number(s.grossSalary) || 0;
    return s?.payType === 'Daily' ? (Number(s.dailyRate) || 0) * 30 : Number(s?.monthlyGross) || 0;
};

const EMPTY_FORM = {
    name: '',
    employeeRollNumber: '',
    payType: 'Monthly',
    monthlyGross: '',
    dailyRate: '',
    isPFApplicable: false,
    isESIApplicable: false,
    isPTApplicable: false,
};

export default function SalaryStructures() {

    const [structures, setStructures] = useState([]);
    const [kpiData, setKpiData] = useState({ totalStructures: 0, totalEmployees: 0 });
    const [split, setSplit] = useState(DEFAULT_SPLIT);
    const [company, setCompany] = useState(null); // null = not configured / unknown
    const [openDialog, setOpenDialog] = useState(false);

    const [snackOpen, setSnackOpen] = useState(false);
    const [snackStatus, setSnackStatus] = useState(false);
    const [snackColor, setSnackColor] = useState(false);
    const [snackMessage, setSnackMessage] = useState('');
    const showSnack = (msg, success) => {
        setSnackMessage(msg); setSnackOpen(true); setSnackColor(success); setSnackStatus(success);
    };

    const [editMode, setEditMode] = useState(false);
    const [selectedStructure, setSelectedStructure] = useState(null);
    const [employeesData, setEmployeesData] = useState([]);
    // Create picker: only active employees that still have no salary structure.
    const [pendingEmployees, setPendingEmployees] = useState([]);
    const [pendingFailed, setPendingFailed] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [selectedEmp, setSelectedEmp] = useState(null);
    const [filterText, setFilterText] = useState('');
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [structureToDelete, setStructureToDelete] = useState(null);
    const websiteSettings = useSelector(selectWebsiteSettings);

    const [formData, setFormData] = useState(EMPTY_FORM);

    // Full-month gross implied by the form (Daily: rate x 30).
    const formGross = formData.payType === 'Daily'
        ? (Number(formData.dailyRate) || 0) * 30
        : Number(formData.monthlyGross) || 0;

    const handleOpenDialog = (structure = null) => {
        if (structure) {
            setEditMode(true);
            setSelectedStructure(structure);
            const isDaily = structure.payType === 'Daily';
            setFormData({
                name: structure.name,
                employeeRollNumber: structure.rollNumber || structure.employeeRollNumber || '',
                payType: isDaily ? 'Daily' : 'Monthly',
                monthlyGross: isDaily ? '' : (structure.monthlyGross ?? ''),
                dailyRate: isDaily ? (structure.dailyRate ?? '') : '',
                isPFApplicable: !!structure.isPFApplicable,
                isESIApplicable: !!structure.isESIApplicable,
                isPTApplicable: !!structure.isPTApplicable,
            });
            const rollNo = structure.rollNumber || structure.employeeRollNumber;
            const emp = employeesData.find(e => e.rollNumber === rollNo);
            setSelectedEmp(emp || null);
        } else {
            setEditMode(false);
            setSelectedStructure(null);
            setSelectedEmp(null);
            setFormData(EMPTY_FORM);
        }
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditMode(false);
        setSelectedStructure(null);
        setSelectedEmp(null);
    };

    useEffect(() => {
        fetchEmployeeData();
        fetchPendingEmployees();
        fetchDashboard();
        fetchCompanyConfig();
    }, []);

    const fetchCompanyConfig = async () => {
        try {
            const res = await http.get(getDeductionsAndCompliance);
            setCompany(res.data && !res.data.error ? res.data.data : null);
        } catch {
            setCompany(null);
        }
        try {
            const res = await http.get(getSalaryComponentConfig);
            if (res.data && !res.data.error) setSplit({ ...DEFAULT_SPLIT, ...res.data.data });
        } catch {
            // keep the built-in split
        }
    };

    const fetchEmployeeData = async () => {
        try {
            const res = await http.get(getEmployees);
            const list = Array.isArray(res.data?.data) ? res.data.data : [];
            setEmployeesData(list.map(e => ({
                ...e,
                rollNumber: e.employeeCode ?? e.rollNumber ?? '',
            })));
        } catch {
            setEmployeesData([]);
        }
    };

    const fetchPendingEmployees = async () => {
        try {
            const res = await http.get(getEmployeesWithoutSalaryStructure);
            const list = Array.isArray(res.data?.data?.employees) ? res.data.data.employees : [];
            setPendingEmployees(list.map(e => ({
                ...e,
                rollNumber: e.employeeCode ?? '',
            })));
            setPendingFailed(false);
        } catch (err) {
            // e.g. 404 while the endpoint isn't deployed yet — fall back to
            // the full list minus employees that already have a structure.
            console.error('GetEmployeesWithoutSalaryStructure failed:', err);
            setPendingFailed(true);
        }
    };

    const createOptions = useMemo(() => {
        if (!pendingFailed) return pendingEmployees;
        const taken = new Set(structures.map(s => s.rollNumber));
        return employeesData.filter(e => !taken.has(e.rollNumber));
    }, [pendingFailed, pendingEmployees, employeesData, structures]);

    const fetchDashboard = async (silent = false) => {
        if (!silent) setIsLoading(true);
        try {
            const res = await http.get(salaryStructureDashboard);
            if (res.data && !res.data.error) {
                const { totalStructures, totalEmployees, salaryStructures, components } = res.data.data;
                setKpiData({ totalStructures, totalEmployees });
                if (components) setSplit(prev => ({ ...prev, ...components }));
                setStructures((salaryStructures || []).map(s => ({
                    ...s,
                    rollNumber: s.employeeCode ?? s.rollNumber ?? '',
                })));
            } else {
                showSnack('Failed to load salary structures', false);
            }
        } catch (err) {
            // 404 = no structure exists yet; that is an empty list, not a failure
            if (err?.response?.status === 404) {
                setStructures([]);
                setKpiData(prev => ({ ...prev, totalStructures: 0 }));
            } else {
                showSnack('Failed to load salary structures', false);
            }
        } finally {
            if (!silent) setIsLoading(false);
        }
    };

    const handleSave = async () => {
        if (!formData.employeeRollNumber) {
            showSnack('Please select an employee', false);
            return;
        }
        const isDaily = formData.payType === 'Daily';
        if (isDaily ? !(Number(formData.dailyRate) > 0) : !(Number(formData.monthlyGross) > 0)) {
            showSnack(isDaily ? 'Daily rate must be greater than 0' : 'Monthly gross must be greater than 0', false);
            return;
        }

        const empCode = editMode
            ? (selectedStructure.employeeCode || selectedStructure.rollNumber || selectedStructure.employeeRollNumber)
            : formData.employeeRollNumber;
        const body = {
            EmployeeCode: empCode,
            PayType: formData.payType,
            ...(isDaily
                ? { DailyRate: Number(formData.dailyRate) }
                : { MonthlyGross: Number(formData.monthlyGross) }),
            IsPFApplicable: formData.isPFApplicable,
            IsESIApplicable: formData.isESIApplicable,
            IsPTApplicable: formData.isPTApplicable,
        };

        setIsSaving(true);
        try {
            const res = editMode
                ? await http.put(updateSalaryStructureByEmployeeCode, body)
                : await http.post(postSalaryStructure, body);
            if (res.data && !res.data.error) {
                await Promise.all([fetchDashboard(), fetchPendingEmployees()]);
                showSnack(editMode ? 'Salary structure updated successfully!' : 'Salary structure created successfully!', true);
                handleCloseDialog();
            } else {
                showSnack(res.data?.message || `Failed to ${editMode ? 'update' : 'create'} salary structure`, false);
            }
        } catch (err) {
            showSnack(apiErrorMessage(err, `Failed to ${editMode ? 'update' : 'create'} salary structure. Please try again.`), false);
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (structure) => {
        const empCode = structure.employeeCode || structure.rollNumber || structure.employeeRollNumber;
        if (!empCode) {
            showSnack('Cannot delete: employee code not found', false);
            return;
        }
        try {
            const res = await http.delete(deleteSalaryStructureByEmployeeCode, {
                params: { employeeCode: empCode },
            });

            if (res.data && !res.data.error) {
                setStructures(prev => prev.filter(s => s.id !== structure.id));
                setKpiData(prev => ({
                    ...prev,
                    totalStructures: Math.max(0, prev.totalStructures - 1),
                }));
                showSnack('Salary structure deleted successfully!', true);
                fetchDashboard(true);
            } else {
                showSnack(res.data?.message || 'Failed to delete salary structure', false);
            }
        } catch {
            showSnack('Failed to delete salary structure. Please try again.', false);
        }
    };

    const fieldSx = {
        '& .MuiOutlinedInput-root': {
            borderRadius: '7px',
            fontSize: '13px',
            bgcolor: '#F9FAFB',
            '&:hover': { bgcolor: '#fff' },
            '&.Mui-focused': { bgcolor: '#fff' },
            '& fieldset': { borderColor: '#E5E7EB' },
            '&.Mui-focused fieldset': { borderColor: PRIMARY, borderWidth: '1.5px' },
        },
        '& .MuiInputLabel-root.Mui-focused': { color: PRIMARY },
    };

    const filtered = useMemo(() => {
        const q = filterText.toLowerCase().trim();
        if (!q) return structures;
        return structures.filter(s =>
            (s.name || '').toLowerCase().includes(q) ||
            (s.rollNumber || s.employeeRollNumber || '').toLowerCase().includes(q)
        );
    }, [structures, filterText]);

    const totals = useMemo(() => {
        let basicSum = 0;
        let grossSum = 0;
        structures.forEach(s => {
            basicSum += Number(s.basicSalary) || 0;
            grossSum += grossOf(s);
        });
        const avgGross = structures.length > 0 ? Math.round(grossSum / structures.length) : 0;
        return { basicSum, grossSum, avgGross };
    }, [structures]);

    const visibleTotals = useMemo(() => {
        let basicSum = 0;
        let grossSum = 0;
        filtered.forEach(s => {
            basicSum += Number(s.basicSalary) || 0;
            grossSum += grossOf(s);
        });
        return { basicSum, grossSum };
    }, [filtered]);

    const handleExport = () => {
        if (!filtered.length) { showSnack('No salary structures to export.', false); return; }
        const rows = filtered.map((s, i) => ({
            'S.No': i + 1,
            'Employee Code': s.rollNumber || s.employeeRollNumber || s.employeeCode || '',
            'Employee Name': s.name || '',
            'Designation': s.designation || '',
            'Department': s.department || '',
            'Pay Type': s.payType || 'Monthly',
            'Monthly Gross': s.payType === 'Daily' ? '' : (Number(s.monthlyGross) || 0),
            'Daily Rate': s.payType === 'Daily' ? (Number(s.dailyRate) || 0) : '',
            'Full-Month Gross': grossOf(s),
            'Basic (preview)': Number(s.basicSalary) || 0,
            'DA (preview)': Number(s.da) || 0,
            'HRA (preview)': Number(s.hra) || 0,
            'Conveyance (preview)': Number(s.conveyanceAllowance) || 0,
            'Special (preview)': Number(s.specialAllowance) || 0,
            'PF': s.isPFApplicable ? 'Yes' : 'No',
            'ESI': s.isESIApplicable ? 'Yes' : 'No',
            'PT': s.isPTApplicable ? 'Yes' : 'No',
        }));
        rows.push({
            'S.No': '', 'Employee Code': '', 'Employee Name': 'TOTAL', 'Designation': '', 'Department': '',
            'Pay Type': '', 'Monthly Gross': '', 'Daily Rate': '', 'Full-Month Gross': visibleTotals.grossSum,
            'Basic (preview)': visibleTotals.basicSum, 'DA (preview)': '', 'HRA (preview)': '',
            'Conveyance (preview)': '', 'Special (preview)': '', 'PF': '', 'ESI': '', 'PT': '',
        });
        const ws = XLSX.utils.json_to_sheet(rows);
        ws['!cols'] = [6, 14, 22, 20, 18, 10, 14, 11, 16, 14, 12, 12, 18, 14, 6, 6, 6].map(wch => ({ wch }));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Salary Structures');
        XLSX.writeFile(wb, `Salary_Structures_${new Date().toISOString().split('T')[0]}.xlsx`);
        showSnack(`Exported ${filtered.length} salary structure${filtered.length === 1 ? '' : 's'}.`, true);
    };

    const coverage = kpiData.totalEmployees > 0
        ? Math.round((kpiData.totalStructures / kpiData.totalEmployees) * 100)
        : 0;

    const kpiCards = [
        {
            label: 'Total Structures',
            value: kpiData.totalStructures,
            sub: `/ ${kpiData.totalEmployees} staff`,
            color: '#135F93', bg: '#E4F0F9',
            icon: AssignmentIcon,
        },
        {
            label: 'Covered Staff',
            value: `${coverage}%`,
            sub: `${kpiData.totalStructures} of ${kpiData.totalEmployees}`,
            color: '#16A34A', bg: '#DCFCE7',
            icon: PeopleAltOutlinedIcon,
        },
        {
            label: 'Avg. Gross Salary',
            value: formatINR(totals.avgGross),
            sub: 'per structure',
            color: '#0EA5E9', bg: '#E0F2FE',
            icon: TrendingUpIcon,
        },
        {
            label: 'Monthly Payout',
            value: formatINR(totals.grossSum),
            sub: 'all structures',
            color: '#F59E0B', bg: '#FFF7ED',
            icon: PaidOutlinedIcon,
        },
    ];

    const searchActive = filterText.trim().length > 0;

    const setCrumbActions = useCrumbActionsSetter();
    const actionsRef = useRef({});
    actionsRef.current = { handleExport, handleOpenDialog };
    const renderToolbar = () => (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
            fullWidth
            variant="outlined"
            placeholder="Search by name or roll number..."
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            slotProps={{
                input: {
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon sx={{ fontSize: 18, color: searchActive ? PRIMARY : '#9CA3AF' }} />
                        </InputAdornment>
                    ),
                    endAdornment: searchActive ? (
                        <InputAdornment position="end">
                            <IconButton size="small" onClick={() => setFilterText('')} sx={{ p: 0.3 }}>
                                <CloseIcon sx={{ fontSize: 14, color: '#9CA3AF' }} />
                            </IconButton>
                        </InputAdornment>
                    ) : null,
                    sx: {
                        padding: '0 12px',
                        borderRadius: '50px',
                        height: '32px',
                        fontSize: '12px',
                    },
                },
            }}
            sx={{
                width: { xs: '100%', sm: 260 },
                '& .MuiOutlinedInput-root': {
                    minHeight: '32px',
                    paddingRight: '3px',
                    backgroundColor: '#fff',
                },
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: websiteSettings.mainColor,
                },
            }}
        />
        <Button
            disableElevation
            onClick={() => actionsRef.current.handleExport()}
            startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
            sx={{
                textTransform: 'none', fontSize: '12.5px', fontWeight: 700,
                color: PRIMARY_DARK, bgcolor: PRIMARY_LIGHT,
                border: `1.5px solid ${PRIMARY_BORDER}`,
                borderRadius: '30px',
                px: 2.2, height: 34,
                boxShadow: 'none',
                '&:hover': {
                    bgcolor: '#D0E5F3',
                    borderColor: PRIMARY,
                    boxShadow: 'none',
                },
            }}
        >
            Export
        </Button>
        <Button
            variant="contained"
            disableElevation
            startIcon={<AddCircleOutlineIcon sx={{ fontSize: 18 }} />}
            onClick={() => actionsRef.current.handleOpenDialog()}
            sx={{
                textTransform: 'none',
                bgcolor: PRIMARY, color: '#fff',
                border: `1.5px solid ${PRIMARY}`,
                borderRadius: '30px',
                fontSize: '12.5px',
                fontWeight: 700,
                px: 2.4, height: 34,
                boxShadow: `0 2px 6px ${PRIMARY}40`,
                '&:hover': {
                    bgcolor: PRIMARY_DARK,
                    borderColor: PRIMARY_DARK,
                    boxShadow: `0 4px 10px ${PRIMARY}55`,
                },
            }}
        >
            Create Structure
        </Button>
    </Box>
    );
    useEffect(() => {
        if (!setCrumbActions) return undefined;
        setCrumbActions(renderToolbar());
        return () => setCrumbActions(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setCrumbActions, filterText, searchActive, websiteSettings.mainColor]);

    return (
        <>
            <SnackBar open={snackOpen} color={snackColor} setOpen={setSnackOpen} status={snackStatus} message={snackMessage} />

            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '16px',
                overflow: 'hidden',
                minHeight: '88vh',
            }}>

                {setCrumbActions ? null : (
                <Box sx={{
                    pb: 2.5, px: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: { xs: 'flex-start', md: 'center' },
                    flexWrap: 'wrap',
                    gap: 1.5,
                }}>
                    <Box>
                        <Typography sx={{ fontSize: '19px', fontWeight: 800, color: '#111827', letterSpacing: '-0.3px' }}>
                            Salary Structures
                        </Typography>
                        <Typography sx={{ fontSize: '12px', color: '#6B7280', mt: 0.3 }}>
                            Set the monthly gross (or daily rate) and statutory applicability for each staff member
                        </Typography>
                    </Box>

                    {renderToolbar()}
                </Box>
                )}

                <Box sx={{ flex: 1, pt:"14px", pb: 2, px:2 }}>

                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        {kpiCards.map((card) => {
                            const Icon = card.icon;
                            return (
                                <Grid size={{ xs: 12, sm: 6, md: 3 }} key={card.label}>
                                    <Box sx={{
                                        p: 2.5,
                                        borderRadius: '7px',
                                        bgcolor: card.bg,
                                        border: `1px solid ${card.color}22`,
                                        boxShadow: '0 1px 3px rgba(16,24,40,0.06)',
                                        height: '100%',
                                        transition: 'transform 0.15s, box-shadow 0.15s',
                                        '&:hover': {
                                            transform: 'translateY(-2px)',
                                            boxShadow: `0 8px 20px ${card.color}22`,
                                        },
                                    }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                                <Typography sx={{
                                                    fontSize: '11px', color: card.color, fontWeight: 700,
                                                    textTransform: 'uppercase', letterSpacing: 0.5,
                                                }}>
                                                    {card.label}
                                                </Typography>
                                                <Typography sx={{
                                                    fontSize: '28px', fontWeight: 800, color: '#0F172A',
                                                    lineHeight: 1.2, mt: 0.5,
                                                }} noWrap>
                                                    {card.value}
                                                </Typography>
                                                <Typography sx={{
                                                    fontSize: '10.5px', color: '#6B7280', fontWeight: 600, mt: 0.4,
                                                }} noWrap>
                                                    {card.sub}
                                                </Typography>
                                            </Box>
                                            <Box sx={{
                                                width: 44, height: 44, borderRadius: '7px',
                                                bgcolor: '#fff', boxShadow: '0 1px 3px rgba(16,24,40,0.08)',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                flexShrink: 0, ml: 1,
                                            }}>
                                                <Icon sx={{ color: card.color, fontSize: 22 }} />
                                            </Box>
                                        </Box>
                                    </Box>
                                </Grid>
                            );
                        })}
                    </Grid>

                    <Card sx={{ border: '1px solid #E1EAF1', borderRadius: '7px', boxShadow: '0 1px 3px rgba(16,24,40,0.05)', bgcolor: '#fff', overflow: 'hidden' }}>
                        <TableContainer sx={{  }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        {[
                                            'S.No', 'Employee', 'Roll No.', 'Pay Type',
                                            'Gross', 'Basic (preview)', 'PF · ESI · PT', 'Actions',
                                        ].map((header) => (
                                            <TableCell
                                                key={header}
                                                sx={{
                                                    fontWeight: 700, fontSize: '10.5px',
                                                    color: '#5B7A94',
                                                    bgcolor: '#F3F8FC',
                                                    textTransform: 'uppercase', letterSpacing: 0.7,
                                                    whiteSpace: 'nowrap', py: 1.5,
                                                    borderBottom: '1px solid #E1EAF1',
                                                }}
                                            >
                                                {header}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {isLoading ? (
                                        <TableRow>
                                            <TableCell colSpan={8} align="center" sx={{ py: 6, borderBottom: 'none' }}>
                                                <SkeletonRows count={5} p={0} />
                                            </TableCell>
                                        </TableRow>
                                    ) : filtered.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={8} align="center" sx={{ py: 6, borderBottom: 'none' }}>
                                                <Box sx={{
                                                    width: 56, height: 56, borderRadius: '50%',
                                                    bgcolor: '#F3F4F6', mx: 'auto', mb: 1.2,
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                }}>
                                                    <AssignmentIcon sx={{ fontSize: 28, color: '#9CA3AF' }} />
                                                </Box>
                                                <Typography sx={{ fontSize: '12px', color: '#6B7280', fontWeight: 600 }}>
                                                    {searchActive ? `No results for "${filterText}"` : 'No salary structures created yet'}
                                                </Typography>
                                                <Typography sx={{ fontSize: '11.5px', color: '#9CA3AF', mt: 0.4 }}>
                                                    {searchActive ? 'Try a different search term' : 'Click "Create Structure" to add the first one'}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : filtered.map((structure, idx) => {
                                        const isDaily = structure.payType === 'Daily';
                                        const basic = Number(structure.basicSalary) || 0;
                                        const rollNo = structure.rollNumber || structure.employeeRollNumber || '—';
                                        const avColor = avatarColorFor(structure.name || '');
                                        const flagChip = (label, on) => (
                                            <Chip
                                                key={label}
                                                label={on ? label : `${label} N/A`}
                                                size="small"
                                                variant={on ? 'filled' : 'outlined'}
                                                sx={{
                                                    height: 20, fontSize: '10px', fontWeight: 700,
                                                    bgcolor: on ? PRIMARY_LIGHT : 'transparent',
                                                    color: on ? PRIMARY : '#9CA3AF',
                                                    borderColor: on ? PRIMARY_BORDER : '#E5E7EB',
                                                }}
                                            />
                                        );

                                        return (
                                            <TableRow
                                                key={structure.id}
                                                sx={{
                                                    bgcolor: idx % 2 ? '#F3F8FC' : '#fff',
                                                    '&:hover': { bgcolor: '#F3F8FC' },
                                                    transition: 'background-color 0.15s',
                                                }}
                                            >
                                                <TableCell sx={{ width: 50, borderBottom: '1px solid #EEF0F6', py: 1.4 }}>
                                                    <Typography sx={{ fontSize: '12px', color: '#9CA3AF', fontWeight: 500 }}>
                                                        {idx + 1}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={{ borderBottom: '1px solid #EEF0F6', py: 1.4 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                                                        <Avatar sx={{
                                                            width: 32, height: 32,
                                                            bgcolor: `${avColor}15`,
                                                            color: avColor,
                                                            fontSize: '11px', fontWeight: 700,
                                                            border: `1px solid ${avColor}33`,
                                                        }}>
                                                            {getInitials(structure.name || '?')}
                                                        </Avatar>
                                                        <Box sx={{ minWidth: 0 }}>
                                                            <Typography sx={{
                                                                fontSize: '13px', fontWeight: 600,
                                                                color: '#111827', whiteSpace: 'nowrap',
                                                            }}>
                                                                {structure.name || '—'}
                                                            </Typography>
                                                            <Typography sx={{ fontSize: '10px', color: '#9CA3AF', fontWeight: 500 }}>
                                                                {structure.designation || 'Staff Member'}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={{ borderBottom: '1px solid #EEF0F6', py: 1.4 }}>
                                                    <Chip
                                                        label={rollNo}
                                                        size="small"
                                                        sx={{
                                                            bgcolor: '#EEF1F6', color: '#475569',
                                                            fontWeight: 600, fontSize: '10.5px', height: 22,
                                                            border: '1px solid #E2E7EF',
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ borderBottom: '1px solid #EEF0F6', py: 1.4 }}>
                                                    <Chip
                                                        label={isDaily ? 'Daily' : 'Monthly'}
                                                        size="small"
                                                        sx={{
                                                            bgcolor: isDaily ? '#FFF7ED' : PRIMARY_LIGHT,
                                                            color: isDaily ? '#C2410C' : PRIMARY,
                                                            border: `1px solid ${isDaily ? '#FED7AA' : PRIMARY_BORDER}`,
                                                            fontWeight: 700, fontSize: '10.5px', height: 22,
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ borderBottom: '1px solid #EEF0F6', py: 1.4 }}>
                                                    <Typography sx={{ fontSize: '13px', fontWeight: 800, color: PRIMARY_DARK }}>
                                                        {isDaily
                                                            ? `${formatINR2(structure.dailyRate)} /day`
                                                            : `${formatINR2(structure.monthlyGross)} /month`}
                                                    </Typography>
                                                    {isDaily && (
                                                        <Typography sx={{ fontSize: '10.5px', color: '#9CA3AF', fontWeight: 600 }}>
                                                            ≈ {formatINR2(grossOf(structure))} /30 days
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell sx={{ borderBottom: '1px solid #EEF0F6', py: 1.4 }}>
                                                    <Tooltip arrow title="Read-only preview of a full 30-day month">
                                                        <Typography sx={{ fontSize: '12.5px', color: '#374151', fontWeight: 600 }}>
                                                            {formatINR2(basic)}
                                                        </Typography>
                                                    </Tooltip>
                                                </TableCell>
                                                <TableCell sx={{ borderBottom: '1px solid #EEF0F6', py: 1.4 }}>
                                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                        {flagChip('PF', structure.isPFApplicable)}
                                                        {flagChip('ESI', structure.isESIApplicable)}
                                                        {flagChip('PT', structure.isPTApplicable)}
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={{ borderBottom: '1px solid #EEF0F6', py: 1.4 }}>
                                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                        <Tooltip arrow title="Edit structure">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleOpenDialog(structure)}
                                                                sx={{
                                                                    bgcolor: '#E4F0F9', borderRadius: '7px',
                                                                    border: '1px solid #B3D3E8',
                                                                    '&:hover': { bgcolor: '#D0E5F3' },
                                                                }}
                                                            >
                                                                <EditIcon sx={{ fontSize: 14, color: '#0E4A75' }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip arrow title="Delete structure">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => { setStructureToDelete(structure); setDeleteConfirmOpen(true); }}
                                                                sx={{
                                                                    bgcolor: '#FEF2F2', borderRadius: '7px',
                                                                    border: '1px solid #FECACA',
                                                                    '&:hover': { bgcolor: '#FEE2E2' },
                                                                }}
                                                            >
                                                                <DeleteIcon sx={{ fontSize: 14, color: '#DC2626' }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {!isLoading && filtered.length > 0 && (
                            <Box sx={{
                                px: 2, py: 1.5, borderTop: '1px solid #CFE0EC', bgcolor: '#F3F8FC',
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                flexWrap: 'wrap', gap: 1.5,
                            }}>
                                <Typography sx={{ fontSize: '12px', color: '#6B7280', fontWeight: 600 }}>
                                    Showing <Box component="span" sx={{ color: PRIMARY_DARK, fontWeight: 800 }}>{filtered.length}</Box> of {structures.length} structures
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.6 }}>
                                        <Typography sx={{ fontSize: '11px', color: '#94A3B8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>Total Basic</Typography>
                                        <Typography sx={{ fontSize: '14px', fontWeight: 800, color: '#0F172A' }}>{formatINR(visibleTotals.basicSum)}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.6 }}>
                                        <Typography sx={{ fontSize: '11px', color: PRIMARY_DARK, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>Total Gross</Typography>
                                        <Typography sx={{ fontSize: '14px', fontWeight: 800, color: PRIMARY_DARK }}>{formatINR(visibleTotals.grossSum)}</Typography>
                                    </Box>
                                </Box>
                            </Box>
                        )}
                    </Card>
                </Box>
            </Box>

            <Dialog
                open={openDialog}
                onClose={handleCloseDialog}
                maxWidth="md"
                fullWidth
                slotProps={{
                    paper: {
                        sx: { borderRadius: '7px', overflow: 'hidden' },
                    },
                }}
            >
                <Box sx={{
                    px: 2.5, py: 2,
                    background: `linear-gradient(135deg, ${PRIMARY_LIGHT} 0%, #fff 70%)`,
                    borderBottom: `1px solid ${PRIMARY_BORDER}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5,
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{
                            width: 40, height: 40, borderRadius: '7px',
                            bgcolor: '#fff', border: `1px solid ${PRIMARY_BORDER}`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            {editMode
                                ? <EditIcon sx={{ color: PRIMARY, fontSize: 20 }} />
                                : <AddCircleOutlineIcon sx={{ color: PRIMARY, fontSize: 20 }} />}
                        </Box>
                        <Box>
                            <Typography sx={{ fontSize: '16px', fontWeight: 800, color: '#111827', lineHeight: 1.1 }}>
                                {editMode ? 'Edit Salary Structure' : 'Create New Salary Structure'}
                            </Typography>
                            <Typography sx={{ fontSize: '11.5px', color: '#6B7280', mt: 0.3 }}>
                                {editMode
                                    ? 'Update the pay and statutory applicability for this staff member'
                                    : 'Set the monthly gross (or daily rate) and statutory applicability'}
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton
                        onClick={handleCloseDialog}
                        sx={{
                            width: 32, height: 32, borderRadius: '7px',
                            bgcolor: '#fff', border: '1px solid #E5E7EB',
                            '&:hover': { bgcolor: '#F9FAFB' },
                        }}
                    >
                        <CloseIcon sx={{ fontSize: 16, color: '#6B7280' }} />
                    </IconButton>
                </Box>

                <DialogContent sx={{ p: 2.5, bgcolor: '#F9FAFB' }}>
                    <Box sx={{
                        p: 2, mb: 2,
                        bgcolor: '#fff', borderRadius: '7px',
                        border: '1px solid #E5E7EB',
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                            <Box sx={{
                                width: 24, height: 24, borderRadius: '7px',
                                bgcolor: '#E4F0F9', border: '1px solid #B3D3E8',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                <PersonOutlineIcon sx={{ fontSize: 14, color: '#0E4A75' }} />
                            </Box>
                            <Typography sx={{
                                fontSize: '11px', fontWeight: 700, color: '#374151',
                                textTransform: 'uppercase', letterSpacing: 0.6,
                            }}>
                                Employee Information
                            </Typography>
                        </Box>
                        <Autocomplete
                            options={editMode ? employeesData : createOptions}
                            noOptionsText={editMode ? 'No employees' : 'All employees have a salary structure'}
                            getOptionLabel={(option) => `${option.companyEmployeeId || option.rollNumber} — ${option.name}`}
                            filterOptions={(opts, { inputValue }) => {
                                const q = inputValue.trim().toLowerCase();
                                if (!q) return opts;
                                return opts.filter((o) =>
                                    (o.companyEmployeeId || '').toLowerCase().includes(q)
                                    || (o.rollNumber || '').toLowerCase().includes(q)
                                    || (o.name || '').toLowerCase().includes(q));
                            }}
                            value={selectedEmp}
                            disabled={editMode}
                            onChange={(_, newVal) => {
                                setSelectedEmp(newVal);
                                setFormData(prev => ({
                                    ...prev,
                                    name: newVal ? `${newVal.rollNumber} - ${newVal.name}` : '',
                                    employeeRollNumber: newVal ? newVal.rollNumber : '',
                                }));
                            }}
                            isOptionEqualToValue={(option, value) => option.rollNumber === value.rollNumber}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Select Employee *"
                                    size="small"
                                    slotProps={{
                                        ...params.slotProps,
                                        inputLabel: { ...params.slotProps?.inputLabel, shrink: true },
                                    }}
                                    sx={fieldSx}
                                />
                            )}
                        />
                        {editMode && (
                            <Typography sx={{ fontSize: '10.5px', color: '#9CA3AF', mt: 0.6, fontStyle: 'italic' }}>
                                Employee cannot be changed when editing an existing structure.
                            </Typography>
                        )}
                    </Box>

                    <Box sx={{
                        p: 2, mb: 2,
                        bgcolor: '#fff', borderRadius: '7px',
                        border: '1px solid #E5E7EB',
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                            <Box sx={{
                                width: 24, height: 24, borderRadius: '7px',
                                bgcolor: '#E4F0F9', border: '1px solid #B3D3E8',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                <CurrencyRupeeIcon sx={{ fontSize: 14, color: '#0E4A75' }} />
                            </Box>
                            <Typography sx={{
                                fontSize: '11px', fontWeight: 700, color: '#374151',
                                textTransform: 'uppercase', letterSpacing: 0.6,
                            }}>
                                Pay
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                            <Typography sx={{ fontSize: '12.5px', fontWeight: 600, color: '#374151' }}>Pay type</Typography>
                            <ToggleButtonGroup
                                size="small"
                                exclusive
                                value={formData.payType}
                                onChange={(_, v) => {
                                    if (!v) return;
                                    setFormData(prev => ({ ...prev, payType: v, monthlyGross: '', dailyRate: '' }));
                                }}
                                sx={{
                                    '& .MuiToggleButton-root': {
                                        textTransform: 'none', fontSize: '12.5px', fontWeight: 700, px: 2, py: 0.5,
                                        color: '#6B7280', borderColor: '#E5E7EB',
                                        '&.Mui-selected': { bgcolor: PRIMARY_LIGHT, color: PRIMARY, borderColor: PRIMARY_BORDER },
                                    },
                                }}
                            >
                                <ToggleButton value="Monthly">Monthly</ToggleButton>
                                <ToggleButton value="Daily">Daily</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>

                        <TextField
                            label={formData.payType === 'Daily' ? 'Daily Rate *' : 'Monthly Gross *'}
                            value={formData.payType === 'Daily' ? formData.dailyRate : formData.monthlyGross}
                            onChange={(e) => setFormData(prev => (
                                prev.payType === 'Daily'
                                    ? { ...prev, dailyRate: e.target.value }
                                    : { ...prev, monthlyGross: e.target.value }
                            ))}
                            size="small"
                            fullWidth
                            type="number"
                            helperText={formData.payType === 'Daily'
                                ? `30-day equivalent ${formatINR2(formGross)} · Paid days × rate each month`
                                : `Per day ${formatINR2(formGross / 30)} (gross ÷ 30) · A full month is always 30 days`}
                            slotProps={{
                                inputLabel: { shrink: true },
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#374151' }}>₹</Typography>
                                        </InputAdornment>
                                    ),
                                    inputProps: { min: 0, step: 'any' },
                                },
                                formHelperText: {
                                    sx: { fontSize: '10.5px', fontWeight: 600, color: '#0E4A75', ml: 0.5, mt: 0.5 },
                                },
                            }}
                            sx={fieldSx}
                        />
                    </Box>

                    <Box sx={{
                        p: 2, mb: 2,
                        bgcolor: '#fff', borderRadius: '7px',
                        border: '1px solid #E5E7EB',
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.2 }}>
                            <Box sx={{
                                width: 24, height: 24, borderRadius: '7px',
                                bgcolor: '#E4F0F9', border: '1px solid #B3D3E8',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                <VerifiedUserOutlinedIcon sx={{ fontSize: 14, color: '#0E4A75' }} />
                            </Box>
                            <Typography sx={{
                                fontSize: '11px', fontWeight: 700, color: '#374151',
                                textTransform: 'uppercase', letterSpacing: 0.6,
                            }}>
                                Statutory Deductions
                            </Typography>
                        </Box>

                        {[
                            {
                                key: 'isPFApplicable', label: 'Provident Fund (PF)', short: 'PF',
                                on: company?.pfEnabled,
                                hint: `${company?.pfEmployeeContribution}% of Basic + DA · ceiling ₹${company?.pfWageCeilingLimit}`,
                            },
                            {
                                key: 'isESIApplicable', label: 'ESI', short: 'ESI',
                                on: company?.esiEnabled,
                                hint: `${company?.esiEmployeeContribution}% of earned gross · below ₹${company?.esiWageCeilingLimit}`,
                            },
                            {
                                key: 'isPTApplicable', label: 'Professional Tax (PT)', short: 'PT',
                                on: company?.ptEnabled,
                                hint: `₹${company?.monthlyPTDeduction}/month at or above ₹${company?.applicableFromSalary}`,
                            },
                        ].map(row => (
                            <Box key={row.key} sx={{ py: 0.4 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                    <Checkbox
                                        size="small"
                                        checked={!!formData[row.key]}
                                        onChange={(e) => setFormData(prev => ({ ...prev, [row.key]: e.target.checked }))}
                                        sx={{ '&.Mui-checked': { color: PRIMARY } }}
                                    />
                                    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#111827', minWidth: 150 }}>
                                        {row.label}
                                    </Typography>
                                    <Typography sx={{ fontSize: '11px', color: '#6B7280' }}>
                                        {company
                                            ? `Company ${row.short}: ${row.on ? 'On' : 'Off'}${row.on ? ` · ${row.hint}` : ''}`
                                            : `Company ${row.short}: not configured`}
                                    </Typography>
                                </Box>
                                {formData[row.key] && company && !row.on && (
                                    <Chip
                                        icon={<WarningAmberIcon sx={{ fontSize: '14px !important' }} />}
                                        label={`Company ${row.short} is switched off — nothing will be deducted until it is enabled.`}
                                        size="small"
                                        sx={{
                                            ml: 4.5, mt: 0.3, height: 22, fontSize: '10.5px', fontWeight: 600,
                                            bgcolor: '#FFF7ED', color: '#C2410C', border: '1px solid #FED7AA',
                                            '& .MuiChip-icon': { color: '#C2410C' },
                                        }}
                                    />
                                )}
                            </Box>
                        ))}
                    </Box>

                    <Paper elevation={0} sx={{
                        p: 2,
                        borderRadius: '7px',
                        border: `1px solid ${PRIMARY_BORDER}`,
                        background: `linear-gradient(135deg, ${PRIMARY_LIGHT} 0%, #fff 70%)`,
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography sx={{
                                    fontSize: '11px', fontWeight: 700, color: PRIMARY_DARK,
                                    textTransform: 'uppercase', letterSpacing: 0.6,
                                }}>
                                    Full-Month Breakdown (30 days, no OT)
                                </Typography>
                                <Typography sx={{ fontSize: '21px', fontWeight: 800, color: PRIMARY_DARK, lineHeight: 1.1, mt: 0.4 }}>
                                    {formatINR2(formGross)}
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, mt: 1 }}>
                                    {SPLIT_PARTS.map(part => (
                                        <Chip
                                            key={part.key}
                                            size="small"
                                            label={`${part.short} ${Number(split[part.key]) || 0}%  ${formatINR2(Math.round(formGross * (Number(split[part.key]) || 0)) / 100)}`}
                                            sx={{
                                                bgcolor: '#fff', color: PRIMARY_DARK, fontWeight: 700,
                                                fontSize: '10.5px', height: 22, border: `1px solid ${PRIMARY_BORDER}`,
                                            }}
                                        />
                                    ))}
                                </Box>
                                <Typography sx={{ fontSize: '10.5px', color: '#4B5563', mt: 0.8 }}>
                                    Derived from the gross by the company split. An actual month = gross ÷ 30 × reporting days.
                                </Typography>
                            </Box>
                            <Box sx={{
                                width: 52, height: 52, borderRadius: '7px',
                                bgcolor: '#fff', border: `1px solid ${PRIMARY_BORDER}`,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                <AccountBalanceWalletIcon sx={{ fontSize: 26, color: PRIMARY }} />
                            </Box>
                        </Box>
                    </Paper>
                </DialogContent>

                <DialogActions sx={{ px: 2.5, py: 1.8, borderTop: '1px solid #E5E7EB', bgcolor: '#fff' }}>
                    <Button
                        onClick={handleCloseDialog}
                        sx={{
                            textTransform: 'none', fontSize: '13px', fontWeight: 600,
                            color: '#374151', borderRadius: '7px',
                            border: '1px solid #E5E7EB', px: 2, height: 38,
                            '&:hover': { bgcolor: '#F9FAFB' },
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={isSaving ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <SaveOutlinedIcon sx={{ fontSize: 18 }} />}
                        onClick={handleSave}
                        disabled={isSaving}
                        sx={{
                            textTransform: 'none', fontSize: '13px', fontWeight: 700,
                            bgcolor: PRIMARY, color: '#fff', borderRadius: '7px',
                            px: 2.5, height: 38,
                            boxShadow: `0 2px 6px ${PRIMARY}33`,
                            '&:hover': { bgcolor: PRIMARY_DARK, boxShadow: `0 4px 12px ${PRIMARY}55` },
                            '&.Mui-disabled': { bgcolor: '#E5E7EB', color: '#9CA3AF' },
                        }}
                    >
                        {isSaving ? 'Saving…' : editMode ? 'Update Structure' : 'Create Structure'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={deleteConfirmOpen}
                onClose={() => setDeleteConfirmOpen(false)}
                maxWidth="xs"
                fullWidth
                slotProps={{
                    paper: {
                        sx: { borderRadius: '7px', overflow: 'hidden' },
                    },
                }}
            >
                <DialogContent sx={{ pt: 3.5, pb: 2, textAlign: 'center' }}>
                    <Box sx={{
                        width: 64, height: 64, borderRadius: '50%',
                        bgcolor: '#FEF2F2', border: '4px solid #FEE2E2',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        mx: 'auto', mb: 1.8,
                    }}>
                        <WarningAmberIcon sx={{ fontSize: 32, color: '#DC2626' }} />
                    </Box>
                    <Typography sx={{ fontSize: '17px', fontWeight: 800, color: '#111827', mb: 0.8 }}>
                        Delete Salary Structure?
                    </Typography>
                    <Typography sx={{ fontSize: '12.5px', color: '#6B7280', px: 1.5, lineHeight: 1.5 }}>
                        Are you sure you want to delete the salary structure for{' '}
                        <strong style={{ color: '#111827' }}>
                            {structureToDelete?.name || 'this employee'}
                        </strong>
                        ? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 2.5, pb: 2.5, gap: 1 }}>
                    <Button
                        fullWidth
                        onClick={() => { setDeleteConfirmOpen(false); setStructureToDelete(null); }}
                        sx={{
                            textTransform: 'none', fontSize: '13px', fontWeight: 600,
                            borderRadius: '7px', height: 38,
                            border: '1px solid #E5E7EB', color: '#374151',
                            '&:hover': { bgcolor: '#F9FAFB' },
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        fullWidth
                        variant="contained"
                        startIcon={<DeleteIcon sx={{ fontSize: 16 }} />}
                        onClick={() => {
                            setDeleteConfirmOpen(false);
                            handleDelete(structureToDelete);
                            setStructureToDelete(null);
                        }}
                        sx={{
                            textTransform: 'none', fontSize: '13px', fontWeight: 700,
                            borderRadius: '7px', height: 38,
                            bgcolor: '#DC2626', color: '#fff',
                            boxShadow: '0 2px 6px rgba(220,38,38,0.3)',
                            '&:hover': { bgcolor: '#B91C1C', boxShadow: '0 4px 12px rgba(220,38,38,0.4)' },
                        }}
                    >
                        Yes, Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
